import * as React from "react";
import { DocumentTextIcon, FolderIcon } from "@heroicons/react/24/outline";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toDefaultDateFormat } from "@/helpers/dates";
import { SharepointItem } from "@/views/sharepoint/types";
import { defaultUserAvatar } from "@/helpers/images";
import { Button } from "@/components/ui/button";
import { CloudDownload } from "lucide-react";
import axios from "axios";
import { useToastDefault } from "@/hooks/use-toast-default";
import { clsx } from "clsx";
import { useNavigate, useParams } from "react-router";
import { SHAREPOINT_DAILY_SYNC_LIMIT, useSharepointSync } from "@/hooks/use-sharepoint-sync";
import TopBarProgress from "react-topbar-progress-indicator";
import { useLazyGetFileDetailsQuery } from "@/api/files";
import * as analytics from "@/services/analytics";

interface Props {
  item: SharepointItem;
}

export const SharepointRow: React.FC<Props> = ({ item }) => {
  const { "*": fseId = "" } = useParams<{ "*": string }>();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const { showToast } = useToastDefault();
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [refetchSharepointFolder] = useLazyGetFileDetailsQuery();
  const { recentItems, addRecentItem, canSync } = useSharepointSync();

  const handleDownload = async () => {
    if (canSync) {
      setIsDownloading(true);

      addRecentItem(item.id);

      analytics.sharepointSync(item.name);

      setIsSyncing(true);
      const response = await axios.post<string>("/pf-document/sharepoint", {
        download_url: item.download_url,
        filename: item.name,
      });
      refetchSharepointFolder({ fse_id: response.data });
      showToast("Success", "Download started, file will be add to My folder");

      navigate(`/drive/${response.data}`);
    } else {
      showToast("Error", `You can only sync ${SHAREPOINT_DAILY_SYNC_LIMIT} files per day`);
    }
  };

  const handleClick = () => {
    if (item.type === "folder") {
      navigate(fseId ? `/sharepoint/${fseId}/${item.id}` : `/sharepoint/${item.id}`);
    }
  };

  const isSynced = recentItems.some(i => i.id === item.id);

  return (
    <>
      {isSyncing && <TopBarProgress />}
      <div className="relative flex flex-col items-start gap-4 p-4 hover:bg-ivory-mist-400 md:flex-row md:items-center md:p-6">
        <div className="relative w-full md:w-1/2 lg:w-7/12">
          <div
            className={clsx("flex items-center", {
              "cursor-pointer": item.type === "folder",
            })}
            onClick={item.type === "folder" ? handleClick : undefined}
          >
            {item.type === "folder" ? (
              <FolderIcon className="mr-2 h-5 w-5 text-yellow-500" />
            ) : (
              <DocumentTextIcon className="mr-2 h-5 w-5 text-blue-500" />
            )}
            <span className="flex truncate font-medium">{item.name}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center text-xs text-gray-500">
            <span className="mr-2">{toDefaultDateFormat(item.created_at)}</span>
            <Avatar className="h-5 w-5">
              <AvatarImage src={defaultUserAvatar} alt={"User"} />
              <AvatarFallback className="text-[10px]">{item.owner[0]}</AvatarFallback>
            </Avatar>
            <span className="ml-2 truncate">{item.owner}</span>
          </div>
        </div>
        {item.type === "file" && (
          <Button
            disabled={isDownloading || isSynced}
            variant="secondary"
            size="sm"
            className="ml-auto w-full text-xs sm:w-auto"
            onClick={handleDownload}
          >
            <CloudDownload className="mr-1 h-4 w-4" />
            {isSynced ? "Synced" : "Sync"}
          </Button>
        )}
      </div>
    </>
  );
};
